import { useEffect, useState } from "react"

import analytics from "./index"
import { ANALYTICS_EVENT_NAMES } from "./events"

// A fresh random id per open of a modal, so events fired during one open (opened, page views, submit)
// can be stitched into a session, and a reopen starts a new one. Pass `modalSessionId` along to any
// related events you fire yourself.
const newModalSessionId = (): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `m_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`

/**
 * Modal analytics in one line: fires `modal.opened` when `open` becomes true (with a fresh
 * `modalSessionId`), and returns that id plus a `trackSubmitted` helper for `modal.submitted`. Every
 * event carries a `name` and the `modalSessionId`, so a modal's events can be grouped per open.
 *
 * @example
 * const { modalSessionId, trackSubmitted } = useModalAnalytics("profile_customize", open)
 * // stitch a related event: analytics.track("...", { modalSessionId })
 * // on successful submit: trackSubmitted()  // or trackSubmitted({ changed: "avatar" })
 */
export const useModalAnalytics = (
  name: string,
  open: boolean
): {
  modalSessionId: string | null
  trackSubmitted: (properties?: Record<string, unknown>) => void
} => {
  // Generate the id the moment `open` flips to true (adjusting state during render), so it is available
  // synchronously to the session's first events and stays stable until the next reopen.
  const [wasOpen, setWasOpen] = useState(open)
  const [modalSessionId, setModalSessionId] = useState<string | null>(
    open ? newModalSessionId() : null
  )
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) setModalSessionId(newModalSessionId())
  }

  useEffect(() => {
    if (open && modalSessionId) {
      analytics.track(ANALYTICS_EVENT_NAMES.MODAL_OPENED, { name, modalSessionId })
    }
  }, [open, modalSessionId, name])

  const trackSubmitted = (properties: Record<string, unknown> = {}) =>
    analytics.track(ANALYTICS_EVENT_NAMES.MODAL_SUBMITTED, { name, modalSessionId, ...properties })

  return { modalSessionId, trackSubmitted }
}

export default useModalAnalytics
